import fetch from 'cross-fetch';
import T from 'i18n-react';
import isObject from 'lodash/isObject';
import isEmpty from 'lodash/isEmpty';
import set from 'lodash/set';

export const fetchJson = (
  input: RequestInfo,
  {
    init,
    jsonBody,
    onSuccess,
    onError,
  }: {
    init?: RequestInit,
    jsonBody?: Object | string | null,
    onSuccess?: ({ response: Response, json: Object }) => void,
    onError?: ({
      response: Response,
      json: Object | null,
      error: Error | null,
      messages?: Array<string> | null,
    }) => void,
  } = {},
): Promise<void> => {
  const reqInit = isObject(init) ? init : {};
  // API accepts json format
  set(reqInit, ['headers', 'Accept'], 'application/json');
  set(reqInit, ['headers', 'X-API-KEY'], process.env.API_KEY);

  if (jsonBody) {
    set(reqInit, ['headers', 'Content-Type'], 'application/json');
    set(
      reqInit,
      ['body'],
      typeof jsonBody === 'string' ? jsonBody : JSON.stringify(jsonBody),
    );
  }

  return fetch(input, reqInit)
    .then(async (response: Response) => {
      if (response.status === 204) {
        // No Content
        onSuccess({ response, json: {} });
      } else {
        const json = await response.json();
        if (response.status >= 400) {
          onError?.({
            response,
            json,
            messages: getStatusMessages(json, response.status),
          });
        } else if (onSuccess) {
          onSuccess({ response, json });
        }
      }
    })
    .catch((error) => {
      onError?.({ response: new Response({}), error });
    });
};

const STATUS_KEYS = {
  401: 'request.unauthorized',
  403: 'request.forbid',
  404: 'request.notFound',
  500: 'request.error',
};

const extractJsonErrorMessages = (json: Object): Array<string> => {
  if ('messages' in json) {
    /*
    { messages: [['Error 1', 'Error 2', ...]] },
    { messages: ['Error 1'] },
    */
    return Object.values(json.messages).flat();
  } else {
    return [];
  }
};

export const getStatusMessages = (
  json: Object | null = null,
  status: number,
): Array<string> => {
  if (isObject(json) && !isEmpty(json)) {
    return extractJsonErrorMessages(json);
  }

  const key = STATUS_KEYS[status];
  return [T.translate(key || 'request.error')];
};
